import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Scale, CheckCircle, Zap, Star, Crown, ArrowLeft, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() { return { headers: { Authorization: `Bearer ${localStorage.getItem("attorneyToken")}` } }; }

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: 199,
    leads: 10,
    icon: Zap,
    color: "border-border",
    badge: null,
    features: [
      "10 leads per month",
      "Shared leads only",
      "Email notifications",
      "Attorney portal access",
      "Lead outcome tracking",
    ],
    cta: "Get Started",
    ctaStyle: "bg-muted hover:bg-muted/80 border border-border text-foreground",
  },
  {
    id: "pro",
    name: "Pro",
    price: 399,
    leads: 25,
    icon: Star,
    color: "border-primary",
    badge: "MOST POPULAR",
    features: [
      "25 leads per month",
      "Shared + Exclusive leads",
      "Priority over Basic tier",
      "Email notifications",
      "Attorney portal access",
      "Lead outcome tracking",
    ],
    cta: "Subscribe — Pro",
    ctaStyle: "bg-primary hover:bg-primary/90 text-primary-foreground",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 799,
    leads: 50,
    icon: Crown,
    color: "border-amber-500/40",
    badge: "BEST VALUE",
    features: [
      "50 leads per month",
      "Shared + Exclusive leads",
      "First priority on ALL cases",
      "Email notifications",
      "Attorney portal access",
      "Lead outcome tracking",
      "Dedicated account support",
    ],
    cta: "Subscribe — Enterprise",
    ctaStyle: "bg-amber-500 hover:bg-amber-400 text-black font-bold",
  },
];

export default function Subscribe() {
  const navigate = useNavigate();
  const [currentPkg, setCurrentPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/attorney/package`, authHeaders())
      .then(r => setCurrentPkg(r.data))
      .catch(() => {});
  }, []);

  const handleSubscribe = async (planId) => {
    setError("");
    setSubscribing(planId);
    setLoading(true);
    try {
      // Simulate Stripe checkout delay
      await new Promise(r => setTimeout(r, 1500));
      const res = await axios.post(`${API}/attorney/subscribe`, { plan: planId }, authHeaders());
      setSuccess(res.data);
      setCurrentPkg({ has_package: true, package_type: planId, leads_limit: res.data.leads_per_month, leads_used: 0, leads_remaining: res.data.leads_per_month });
    } catch (err) {
      setError(err.response?.data?.detail || "Subscription failed. Please try again.");
    } finally {
      setLoading(false);
      setSubscribing(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/attorney/dashboard")} className="p-1.5 rounded-lg hover:bg-muted transition text-muted-foreground">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center">
            <Scale className="w-3.5 h-3.5 text-primary" />
          </div>
          <span className="font-semibold text-sm">Choose Your Plan</span>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold tracking-tight mb-3">Get More Clients</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Subscribe to receive pre-qualified legal leads matched to your practice area. Cancel anytime.
          </p>
          {currentPkg?.has_package && (
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Active: <span className="font-semibold capitalize">{currentPkg.package_type}</span> plan — {currentPkg.leads_remaining} leads remaining
            </div>
          )}
        </div>

        {success && (
          <div className="mb-8 p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="font-semibold text-lg">Subscription Activated!</p>
            <p className="text-sm mt-1">{success.message}</p>
            <button onClick={() => navigate("/attorney/dashboard")} className="mt-4 py-2 px-6 rounded-lg bg-emerald-500 text-black font-semibold text-sm hover:bg-emerald-400 transition">
              Go to Dashboard
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map(plan => {
            const Icon = plan.icon;
            const isCurrent = currentPkg?.package_type === plan.id;
            const isSubscribing = subscribing === plan.id;
            return (
              <div key={plan.id} className={`relative rounded-2xl border-2 bg-card p-6 flex flex-col ${plan.color} ${isCurrent ? "ring-2 ring-primary/30" : ""}`}>
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`text-[10px] font-bold px-3 py-1 rounded-full ${plan.id === "pro" ? "bg-primary text-primary-foreground" : "bg-amber-500 text-black"}`}>
                      {plan.badge}
                    </span>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <span className="text-[10px] font-bold px-3 py-1 rounded-full bg-emerald-500 text-black">CURRENT</span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.id === "enterprise" ? "bg-amber-500/15 border border-amber-500/30" : "bg-primary/10 border border-primary/20"}`}>
                    <Icon className={`w-5 h-5 ${plan.id === "enterprise" ? "text-amber-400" : "text-primary"}`} />
                  </div>
                  <div>
                    <p className="font-bold text-base">{plan.name}</p>
                    <p className="text-xs text-muted-foreground">{plan.leads} leads/month</p>
                  </div>
                </div>

                <div className="mb-5">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </div>

                <ul className="space-y-2.5 flex-1 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => !isCurrent && handleSubscribe(plan.id)}
                  disabled={loading || isCurrent}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${isCurrent ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 cursor-default" : plan.ctaStyle}`}
                >
                  {isCurrent ? "Current Plan" : isSubscribing ? "Processing..." : plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Trust badges */}
        <div className="mt-10 flex items-center justify-center gap-6 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Secure payment</span>
          <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Cancel anytime</span>
          <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Leads start immediately</span>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Questions? Contact <a href="mailto:partners@icantfindalawyer.com" className="text-primary hover:underline">partners@icantfindalawyer.com</a>
        </p>
      </div>
    </div>
  );
}
